import clsx from 'clsx';
import type React from 'react';
import { memo, type Ref, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { sanitizeHtml } from '../../common/functions/sanitize-html';

interface InnerHTMLDivProps extends React.HTMLAttributes<HTMLDivElement> {
  html: string;
  ref?: Ref<HTMLDivElement>;
  /** When true (default) the html is sanitised with DOMPurify before
   *  insertion.  Set to false when the caller has already applied custom
   *  sanitisation (e.g. useDOMPurify) to avoid double-processing. */
  sanitize?: boolean;
}

/**
 * Sets innerHTML via a ref + useEffect instead of dangerouslySetInnerHTML.
 *
 * React 19 re-applies dangerouslySetInnerHTML on every render, which
 * destroys and recreates the DOM subtree.  When the HTML contains resources
 * resolved by a service worker (e.g. images), each DOM reset triggers a
 * fresh load → layout shift → parent re-render → infinite loop.
 *
 * By assigning innerHTML imperatively only when the html string changes,
 * the existing DOM (and already-loaded images) is left untouched across
 * re-renders.
 *
 * All incoming HTML is sanitised with DOMPurify before insertion by default.
 * Pass `sanitize={false}` when the caller has already sanitised the HTML.
 */
const InnerHTMLDiv = memo(({ html, ref, className, sanitize = true, ...props }: InnerHTMLDivProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => divRef.current as HTMLDivElement);

  const processedHtml = useMemo(() => (sanitize ? sanitizeHtml(html) : html), [html, sanitize]);

  useEffect(() => {
    if (divRef.current) {
      divRef.current.innerHTML = processedHtml;
    }
  }, [processedHtml]);

  return <div ref={divRef} className={clsx('inner-html-content', className)} {...props} />;
});

InnerHTMLDiv.displayName = 'InnerHTMLDiv';

export default InnerHTMLDiv;
